import { mount, TagInstance } from 'riot';
import * as cadastreReportActions from '../../actions/cadastreReport';
import * as landRegistryActions from '../../actions/landRegistry';
import * as cadastreReportConstants from '../../constants/cadastreReport';
import * as landRegistryConstants from '../../constants/landRegistry';
import { isCadastreReportProduct } from '../../services/cadastreReport';
import { isLandRegistryLookupProduct } from '../../services/landRegistryLookup';
import cadastreReportStore from '../../stores/cadastreReport';
import landRegistryStore from '../../stores/landRegistry';
import { LandRegistryComponentsToMount, LandRegistryPartAndState } from '../../typings/LandRegistryLookup';

const { events: cadastreReportEvents } = cadastreReportConstants;
const { events: landRegistryEvents, tags: landRegistryTags } = landRegistryConstants;

const addAndMountLandRegistryTag = (
  domElementToMountOn: HTMLElement,
  componentToMount: LandRegistryPartAndState
): TagInstance => {
  const landRegistryDOMElement: HTMLElement = document.createElement(landRegistryTags.AMBITA_LAND_REGISTRY);
  domElementToMountOn.appendChild(landRegistryDOMElement);

  return mount(landRegistryDOMElement, landRegistryTags.AMBITA_LAND_REGISTRY, {
    landRegistryPart: componentToMount.landRegistryPart,
    landRegistryState: componentToMount.landRegistryState
  })[0];
};

const registerLoadingLandRegistryTags = (landRegistryComponentsToMount: LandRegistryPartAndState[]): void => {
  landRegistryComponentsToMount.forEach((componentToMount) => {
    landRegistryActions.registerTagLoading(componentToMount.landRegistryPart, componentToMount.landRegistryState);
  });
};

const retrieveLandRegistries = (
  landRegistryComponentsToMount: LandRegistryPartAndState[],
  propertyKey: string
): void => {
  landRegistryComponentsToMount.forEach((componentToMount) => {
    landRegistryActions.retrieveLandRegistry(
      componentToMount.landRegistryPart,
      propertyKey,
      componentToMount.landRegistryState
    );
  });
};

const addAndMountCadastreReportTag = (
  domElementToMountOn: HTMLElement,
  componentToMount: string
): TagInstance => {
  const cadastreReportDomElement: HTMLElement = document.createElement(componentToMount);
  domElementToMountOn.appendChild(cadastreReportDomElement);

  return mount(cadastreReportDomElement, componentToMount, { multipleReport: true })[0];
};

export const init = (tag): void => {
  const triggerLoadFinish = (): void => {
    if (landRegistryStore.areAllLandRegistryTagsFinishedLoading() &&
      cadastreReportStore.isCadastreReportFinishedLoading()) {
      tag.opts.onTaskDone();
      tag.update();
    }
  };

  const initLandRegistryHandling = (): void => {
    const landRegistryComponentsToMount: LandRegistryComponentsToMount = tag.opts.landRegistryComponentsToMount;
    const landRegistryComponentsToMountArray: LandRegistryPartAndState[] = Object.keys(landRegistryComponentsToMount)
      .map(productCode => landRegistryComponentsToMount[productCode]);

    registerLoadingLandRegistryTags(landRegistryComponentsToMountArray);
    retrieveLandRegistries(landRegistryComponentsToMountArray, tag.opts.propertyKey);
  };

  const initProducts = (): void => {
    tag.landRegistryTags = [];
    tag.cadastreReportTags = [];

    tag.opts.products.forEach((product: string) => {
      if (isLandRegistryLookupProduct(product)) {
        const landRegistryComponentToMount = tag.opts.landRegistryComponentsToMount[product];
        const landRegistryTag = addAndMountLandRegistryTag(tag.root, landRegistryComponentToMount);

        tag.landRegistryTags.push(landRegistryTag);
      } else if (isCadastreReportProduct(product)) {
        const cadastreReportComponentToMount = tag.opts.cadastreReportComponentsToMount[product];
        const cadastreReportTag = addAndMountCadastreReportTag(tag.root, cadastreReportComponentToMount);

        tag.cadastreReportTags.push(cadastreReportTag);
      }
    });

    if (tag.landRegistryTags.length > 0) {
      initLandRegistryHandling();
    }

    if (tag.cadastreReportTags.length > 0) {
      cadastreReportActions.requestCadastreLookup(tag.opts.propertyKey);
    }
  };

  tag.on('mount', () => {
    initProducts();
    cadastreReportStore.on(cadastreReportEvents.CADASTRE_LOOKUP_LOADED, triggerLoadFinish);
    landRegistryStore.on(landRegistryEvents.LAND_REGISTRY_LOADING_COMPLETE, triggerLoadFinish);
  });

  tag.on('unmount', () => {
    cadastreReportStore.off(cadastreReportEvents.CADASTRE_LOOKUP_LOADED, triggerLoadFinish);
    landRegistryStore.off(landRegistryEvents.LAND_REGISTRY_LOADING_COMPLETE, triggerLoadFinish);

    tag.landRegistryTags.forEach((landRegistryTag) => {
      landRegistryTag.unmount();
    });

    tag.cadastreReportTags.forEach((cadastreReportTag) => {
      cadastreReportTag.unmount();
    });
  });
};
